import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AffiliateShell from "@/components/feature/AffiliateShell";
import MemberAvatar from "@/components/feature/MemberAvatar";
import { fetchApprovedMessages, type ApprovedMessage } from "@/services/admin";
import { fetchCurrentProfile, type AppProfile } from "@/services/auth";
import {
  fetchAffiliateDashboard,
  fetchLinkCreatedAt,
  fetchPersonalityProfiles,
  logShare,
  referralLink,
  deltaPercent,
  type AffiliateDashboard,
  type ShareChannel,
} from "@/services/affiliate";
import { fetchMyQuizResult } from "@/services/quiz";
import { downloadDataUrl, QR_COLORS, renderCardPng, renderQrCanvas, type QrStyle } from "@/lib/qr";

const PURPLE = "#7C3AED";
const TAGLINE = "Transformando vidas con conocimiento y acción";

type Network = "whatsapp" | "facebook" | "instagram" | "email";

const NETWORKS: { key: Network; label: string; icon: string; iconCls: string; text: string }[] = [
  {
    key: "whatsapp",
    label: "Para WhatsApp",
    icon: "ri-whatsapp-fill",
    iconCls: "text-[#25D366]",
    text: "¡Hola! 😊 Te comparto algo que me está ayudando a crecer personal y profesionalmente. ¡Échale un vistazo!",
  },
  {
    key: "facebook",
    label: "Para Facebook",
    icon: "ri-facebook-circle-fill",
    iconCls: "text-[#1877F2]",
    text: "Descubre Vívelo AI, la plataforma que te ayuda a crecer, aprender y construir tu camino con propósito. ¡Te va a encantar!",
  },
  {
    key: "instagram",
    label: "Para Instagram",
    icon: "ri-instagram-fill",
    iconCls: "text-[#E1306C]",
    text: "Aprende, crece y transforma tu vida con Vívelo AI ✨ Accede a cursos, a una guía con IA y a una comunidad increíble. ¡Únete hoy!",
  },
  {
    key: "email",
    label: "Para Email",
    icon: "ri-mail-line",
    iconCls: "text-sky-500",
    text: "Te invito a conocer Vívelo AI, una plataforma diseñada para ayudarte a alcanzar tus metas y avanzar a tu ritmo.",
  },
];

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <section className={`bg-white border border-slate-200 rounded-2xl ${className}`}>{children}</section>;
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="fixed inset-0 bg-[#0B1F3A]/70 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto bg-white rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-heading font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} aria-label="Cerrar" className="w-8 h-8 rounded-full hover:bg-slate-100 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

function loadStyle(): QrStyle {
  try {
    const raw = localStorage.getItem("vv-qr-style");
    if (raw) {
      const p = JSON.parse(raw) as Partial<QrStyle>;
      if (typeof p.color === "string" && QR_COLORS.some((c) => c.value === p.color)) {
        return { color: p.color, withLogo: p.withLogo !== false };
      }
    }
  } catch {
    /* sin almacenamiento: usa el estilo por defecto */
  }
  return { color: "#0B1F3A", withLogo: true };
}

export default function MiLink() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<AppProfile | null>(null);
  const [data, setData] = useState<AffiliateDashboard | null>(null);
  const [createdAt, setCreatedAt] = useState<Date | null>(null);
  const [archetypeLabel, setArchetypeLabel] = useState<string>("Miembro Vívelo");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [qrStyle, setQrStyle] = useState<QrStyle>(loadStyle);
  const [qrUrl, setQrUrl] = useState("");
  const [selected, setSelected] = useState<Network>("whatsapp");
  const [modal, setModal] = useState<null | "how" | "style" | "messages">(null);
  const [approved, setApproved] = useState<ApprovedMessage[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const carousel = useRef<HTMLDivElement>(null);

  const link = data ? referralLink(data.code) : "";

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    let active = true;
    Promise.all([fetchCurrentProfile(), fetchAffiliateDashboard(0), fetchLinkCreatedAt()])
      .then(async ([p, d, c]) => {
        if (!active) return;
        setProfile(p);
        setData(d);
        setCreatedAt(c);
        try {
          const quiz = await fetchMyQuizResult();
          const key = quiz && "archetypeKey" in quiz ? (quiz as { archetypeKey?: string }).archetypeKey : undefined;
          if (key) {
            const all = await fetchPersonalityProfiles();
            const found = all.find((x) => x.key === key);
            if (active && found) setArchetypeLabel(found.label);
          }
        } catch {
          /* el perfil del test es opcional en la tarjeta */
        }
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "No se pudo cargar tu link."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // QR con el estilo elegido
  useEffect(() => {
    if (!link) return;
    let active = true;
    renderQrCanvas(link, qrStyle, 640)
      .then((c) => active && setQrUrl(c.toDataURL("image/png")))
      .catch(() => undefined);
    try {
      localStorage.setItem("vv-qr-style", JSON.stringify(qrStyle));
    } catch {
      /* ignorar */
    }
    return () => {
      active = false;
    };
  }, [link, qrStyle]);

  const track = (channel: ShareChannel) => {
    logShare(channel).catch(() => undefined);
  };

  const messageFor = (n: Network) => NETWORKS.find((x) => x.key === n)!.text;

  const copy = async (text: string, okMsg: string, channel: ShareChannel = "copy") => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(okMsg);
    } catch {
      showToast("No se pudo copiar. Selecciona el texto y cópialo.");
    }
    track(channel);
  };

  const shareSelected = () => {
    const text = messageFor(selected);
    if (selected === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`, "_blank", "noopener,noreferrer");
      track("whatsapp");
    } else if (selected === "facebook") {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}&quote=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
      track("share");
    } else if (selected === "email") {
      window.location.href = `mailto:?subject=${encodeURIComponent("Te invito a conocer Vívelo AI")}&body=${encodeURIComponent(`${text}\n\n${link}`)}`;
      track("share");
    } else {
      copy(`${text} ${link}`, "Mensaje copiado. Pégalo en tu publicación o historia de Instagram.", "share");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Vívelo AI", text: messageFor("whatsapp"), url: link });
        track("share");
      } catch {
        /* cerró el menú */
      }
    } else {
      await copy(link, "Link copiado");
    }
  };

  const downloadQr = () => {
    if (!qrUrl) return;
    downloadDataUrl(qrUrl, "vivelo-qr.png");
    track("qr");
  };

  const printQr = () => {
    if (!qrUrl) return;
    const w = window.open("", "_blank", "width=700,height=900");
    if (!w) {
      showToast("Tu navegador bloqueó la ventana de impresión.");
      return;
    }
    const name = (profile?.fullName ?? "").replace(/[<>&"]/g, "");
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Mi QR Vívelo AI</title><style>body{font-family:Arial,sans-serif;text-align:center;padding:40px}img{width:420px;height:420px}h1{font-size:28px;margin:0 0 8px}p{color:#475569}</style></head><body><h1>Escanéame para registrarte</h1><p>${name ? `Te invita ${name}` : ""}</p><img src="${qrUrl}" alt="QR"/><p>${link.replace(/[<>&"]/g, "")}</p></body></html>`
    );
    w.document.close();
    w.focus();
    window.setTimeout(() => w.print(), 400);
    track("qr");
  };

  const downloadCard = async () => {
    if (!profile || !link) return;
    try {
      const png = await renderCardPng({
        name: profile.fullName ?? "Miembro Vívelo",
        subtitle: archetypeLabel,
        tagline: TAGLINE,
        link,
        avatarUrl: profile.avatarUrl,
        qr: qrStyle,
      });
      downloadDataUrl(png, "vivelo-tarjeta-digital.png");
      track("qr");
    } catch {
      showToast("No se pudo crear la tarjeta. Intenta de nuevo.");
    }
  };

  const openMessages = async () => {
    setModal("messages");
    try {
      setApproved((await fetchApprovedMessages()).filter((m) => m.active));
    } catch {
      showToast("No se pudieron cargar los mensajes.");
    }
  };

  const scrollCarousel = () => carousel.current?.scrollBy({ left: 260, behavior: "smooth" });

  const createdText = useMemo(
    () => (createdAt ? `Enlace creado el ${createdAt.getDate()} de ${MONTHS[createdAt.getMonth()]} de ${createdAt.getFullYear()}` : "Enlace activo"),
    [createdAt]
  );

  if (loading) {
    return (
      <AffiliateShell>
        <div className="py-24 text-center text-slate-500">
          <i className="ri-loader-4-line animate-spin text-3xl"></i>
        </div>
      </AffiliateShell>
    );
  }
  if (error || !data || !profile) {
    return (
      <AffiliateShell>
        <div className="max-w-xl mx-auto mt-16 bg-white border border-dashed border-rose-300 rounded-2xl p-10 text-center text-rose-600 text-sm">
          {error ?? "Todavía no tienes un link personal en esta organización."}
        </div>
      </AffiliateShell>
    );
  }

  const k = data.kpis;
  const visitsDelta = deltaPercent(k.visits);
  const impact = [
    { label: "Visitas", value: k.visits.total, icon: "ri-eye-line", tint: "bg-sky-50 text-sky-600" },
    { label: "Test completados", value: k.tests.total, icon: "ri-clipboard-line", tint: "bg-emerald-50 text-emerald-600" },
    { label: "Prospectos", value: k.prospects.total, icon: "ri-star-line", tint: "bg-amber-50 text-amber-500" },
    { label: "Leads calientes", value: k.hot.total, icon: "ri-fire-line", tint: "bg-orange-50 text-orange-500" },
    { label: "Nuevos afiliados", value: k.clients.total, icon: "ri-team-line", tint: "bg-violet-50 text-violet-600" },
  ];
  const goalPct = Math.min(100, Math.round((data.invitesGoal.done / data.invitesGoal.goal) * 100));

  return (
    <AffiliateShell>
      <div className="max-w-6xl mx-auto flex flex-col gap-4">
        {/* Encabezado */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-[#0B1F3A] flex items-center gap-2">
              <i className="ri-links-line" style={{ color: PURPLE }}></i> Mi Link / QR
            </h1>
            <p className="text-sm text-slate-600 mt-1">Comparte tu enlace personal y ayuda a más personas a transformar su vida.</p>
          </div>
          <button onClick={() => setModal("how")} className="px-4 py-2.5 rounded-lg bg-white border text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-violet-50" style={{ color: PURPLE, borderColor: PURPLE }}>
            <i className="ri-question-line mr-1.5"></i>¿Cómo compartir?
          </button>
        </div>

        {/* Enlace + QR */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h2 className="font-heading font-bold text-slate-900">Tu enlace personal</h2>
            <p className="text-xs text-slate-500 mt-1">Este es tu link único. Cada persona que entre por aquí quedará registrada como tu prospecto.</p>
            <div className="mt-4 flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-3">
              <input readOnly value={link} aria-label="Tu enlace personal" onFocus={(e) => e.currentTarget.select()} className="flex-1 min-w-0 bg-transparent text-sm text-slate-800 outline-none" />
              <button onClick={() => copy(link, "Link copiado")} aria-label="Copiar link" className="w-9 h-9 rounded-lg hover:bg-slate-100 cursor-pointer" style={{ color: PURPLE }}>
                <i className="ri-file-copy-line text-xl"></i>
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => {
                  window.open(`https://wa.me/?text=${encodeURIComponent(`${messageFor("whatsapp")} ${link}`)}`, "_blank", "noopener,noreferrer");
                  track("whatsapp");
                }}
                className="px-4 py-2.5 rounded-lg bg-[#16A34A] hover:bg-[#15803D] text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-1.5"></i>Compartir por WhatsApp
              </button>
              <button onClick={nativeShare} className="px-4 py-2.5 rounded-lg border text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-violet-50" style={{ color: PURPLE, borderColor: PURPLE }}>
                <i className="ri-share-line mr-1.5"></i>Compartir enlace
              </button>
            </div>
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-emerald-50 text-emerald-700 text-xs px-3 py-2.5">
              <i className="ri-calendar-check-line"></i> {createdText}
            </div>
          </Card>

          <Card className="p-5">
            <h2 className="font-heading font-bold text-slate-900">Tu Código QR</h2>
            <p className="text-xs text-slate-500 mt-1">Escanéalo o descárgalo para compartir en redes, presentaciones y material impreso.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-4">
              <div className="w-44 h-44 flex-shrink-0 rounded-xl border border-slate-200 p-2 bg-white">
                {qrUrl ? <img src={qrUrl} alt="Código QR de tu link" className="w-full h-full" /> : <div className="w-full h-full bg-slate-100 animate-pulse rounded-lg"></div>}
              </div>
              <div className="flex-1 flex flex-col gap-2 justify-center">
                <button onClick={downloadQr} className="w-full text-left px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-800 hover:bg-slate-50 cursor-pointer">
                  <i className="ri-download-2-line mr-2"></i>Descargar QR
                </button>
                <button onClick={printQr} className="w-full text-left px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-800 hover:bg-slate-50 cursor-pointer">
                  <i className="ri-printer-line mr-2"></i>Imprimir
                </button>
                <button onClick={() => setModal("style")} className="w-full text-left px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-800 hover:bg-slate-50 cursor-pointer">
                  <i className="ri-palette-line mr-2"></i>Personalizar
                </button>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-500">
              <i className="ri-lightbulb-line text-emerald-500"></i> Tip: imprímelo y colócalo en tu material o eventos.
            </p>
          </Card>
        </div>

        {/* Mensajes sugeridos */}
        <Card className="p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-heading font-bold text-slate-900">Mensajes sugeridos</h2>
              <p className="text-xs text-slate-500 mt-0.5">Usa estos mensajes para invitar y conectar de forma efectiva.</p>
            </div>
            <button onClick={openMessages} className="text-xs font-semibold cursor-pointer whitespace-nowrap" style={{ color: PURPLE }}>
              Ver todos los mensajes <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
          <div className="mt-4 relative">
            <div ref={carousel} className="flex gap-3 overflow-x-auto vv-scrollbar-hidden snap-x pr-10 pb-1" role="listbox" aria-label="Mensajes por red social">
              {NETWORKS.map((n) => {
                const on = selected === n.key;
                return (
                  <button
                    key={n.key}
                    role="option"
                    aria-selected={on}
                    onClick={() => setSelected(n.key)}
                    className={`snap-start flex-shrink-0 w-60 text-left rounded-xl border p-4 cursor-pointer transition-colors ${on ? "border-violet-400 bg-violet-50/60" : "border-slate-200 hover:border-violet-200"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-2 text-xs font-bold text-slate-900">
                        <i className={`${n.icon} text-xl ${n.iconCls}`}></i>
                        {n.label}
                      </span>
                      {on ? <i className="ri-checkbox-circle-fill text-lg" style={{ color: PURPLE }}></i> : null}
                    </div>
                    <p className="mt-3 text-xs leading-relaxed text-slate-700">{n.text}</p>
                  </button>
                );
              })}
            </div>
            <button onClick={scrollCarousel} aria-label="Ver más mensajes" className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-slate-200 shadow items-center justify-center cursor-pointer" style={{ color: PURPLE }}>
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => copy(`${messageFor(selected)} ${link}`, "Mensaje copiado")} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold cursor-pointer">
              <i className="ri-file-copy-line mr-1.5"></i>Copiar mensaje con mi link
            </button>
            <button onClick={shareSelected} className="px-4 py-2 rounded-lg text-white text-xs font-semibold cursor-pointer" style={{ background: PURPLE }}>
              <i className="ri-send-plane-line mr-1.5"></i>Compartir en {NETWORKS.find((n) => n.key === selected)!.label.replace("Para ", "")}
            </button>
          </div>
        </Card>

        {/* Impacto + acciones + tarjeta */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <Card className="lg:col-span-5 p-5 flex flex-col">
            <h2 className="font-heading font-bold text-slate-900">Tu impacto hasta hoy</h2>
            <p className="text-xs text-slate-500">Personas que han ingresado por tu link</p>
            <div className="mt-4 grid grid-cols-5 gap-1 text-center">
              {impact.map((i) => (
                <div key={i.label} className="flex flex-col items-center gap-1">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${i.tint}`}>
                    <i className={`${i.icon} text-lg`}></i>
                  </span>
                  <span className="font-heading font-extrabold text-xl text-slate-900">{i.value.toLocaleString("es")}</span>
                  <span className="text-[10px] leading-tight text-slate-500">{i.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-auto pt-4 flex items-center justify-between gap-2 border-t border-slate-100 mt-4">
              <span className={`text-[11px] font-semibold ${visitsDelta !== null && visitsDelta < 0 ? "text-rose-500" : "text-emerald-600"}`}>
                {visitsDelta === null ? "Sin datos de la semana pasada" : `${visitsDelta >= 0 ? "↑" : "↓"} ${Math.abs(visitsDelta)}% ${visitsDelta >= 0 ? "más" : "menos"} que la semana pasada`}
              </span>
              <button onClick={() => navigate("/app/mis-prospectos")} className="px-3 py-2 rounded-lg border text-xs font-semibold cursor-pointer whitespace-nowrap hover:bg-violet-50" style={{ color: PURPLE, borderColor: PURPLE }}>
                Ver mis prospectos
              </button>
            </div>
          </Card>

          <Card className="lg:col-span-4 p-5">
            <h2 className="font-heading font-bold text-slate-900">Acciones rápidas</h2>
            <ul className="mt-2 divide-y divide-slate-100">
              {[
                { icon: "ri-file-copy-line", tint: "bg-sky-50 text-sky-600", title: "Copiar mi link", desc: "Copia tu enlace al portapapeles", run: () => copy(link, "Link copiado") },
                { icon: "ri-share-line", tint: "bg-emerald-50 text-emerald-600", title: "Compartir en redes sociales", desc: "Publica tu link en tus redes favoritas", run: nativeShare },
                {
                  icon: "ri-mail-line",
                  tint: "bg-sky-50 text-sky-600",
                  title: "Agregar a mi firma de email",
                  desc: "Incluye tu link en tus correos",
                  run: () => copy(`${profile.fullName ?? ""} · Vívelo AI\nConoce Vívelo AI: ${link}`, "Firma copiada. Pégala en la configuración de tu correo.", "copy"),
                },
                { icon: "ri-profile-line", tint: "bg-violet-50 text-violet-600", title: "Descargar tarjeta digital", desc: "Comparte tu tarjeta con tu QR y link", run: downloadCard },
              ].map((a) => (
                <li key={a.title}>
                  <button onClick={a.run} className="w-full flex items-center gap-3 py-3 text-left cursor-pointer hover:bg-slate-50 rounded-lg px-1">
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${a.tint}`}>
                      <i className={`${a.icon} text-lg`}></i>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-slate-900">{a.title}</span>
                      <span className="block text-[11px] text-slate-500">{a.desc}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <section className="lg:col-span-3 rounded-2xl p-5 text-center text-white bg-gradient-to-b from-[#0B1F3A] to-[#1B1446] flex flex-col items-center">
            <div className="text-[11px] text-slate-300">Vista previa tarjeta digital</div>
            <div className="mt-3">
              <MemberAvatar name={profile.fullName ?? "Tú"} src={profile.avatarUrl} />
            </div>
            <div className="mt-2 font-heading font-bold text-sm">{profile.fullName ?? "Miembro Vívelo"}</div>
            <div className="text-[11px] text-indigo-300">{archetypeLabel}</div>
            <div className="mt-2 text-[10px] text-slate-300 leading-snug max-w-[160px]">{TAGLINE}</div>
            <div className="mt-3 w-24 h-24 bg-white rounded-lg p-1.5">{qrUrl ? <img src={qrUrl} alt="" className="w-full h-full" /> : null}</div>
            <div className="mt-3 font-heading font-extrabold text-sm tracking-wide">VÍVELO AI</div>
          </section>
        </div>

        {/* Recompensas + reto semanal */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          <section className="lg:col-span-7 rounded-2xl p-5 flex items-center gap-4 border border-violet-100 bg-violet-50/60">
            <span className="w-14 h-14 rounded-full flex items-center justify-center text-white flex-shrink-0" style={{ background: PURPLE }}>
              <i className="ri-gift-2-fill text-2xl"></i>
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading font-bold text-slate-900">¡Gana recompensas compartiendo!</h2>
              <p className="text-xs text-slate-600 mt-0.5">Por cada persona que se registre con tu link y realice su test, ganas puntos que puedes canjear por premios.</p>
            </div>
            <button onClick={() => navigate("/app/afiliado")} className="px-4 py-2 rounded-lg bg-white border text-xs font-semibold cursor-pointer whitespace-nowrap" style={{ color: PURPLE, borderColor: PURPLE }}>
              Ver recompensas
            </button>
          </section>
          <Card className="lg:col-span-5 p-5 flex items-center gap-4">
            <span className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0" style={{ color: PURPLE }}>
              <i className="ri-focus-3-line text-2xl"></i>
            </span>
            <div className="flex-1 min-w-0">
              <h2 className="font-heading font-bold text-slate-900">Reto semanal</h2>
              <p className="text-[11px] text-slate-500">Invita a {data.invitesGoal.goal} personas esta semana y gana 100 puntos extra.</p>
              <div className="mt-2 flex items-center gap-3">
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden" role="progressbar" aria-valuenow={goalPct} aria-valuemin={0} aria-valuemax={100}>
                  <div className="h-full rounded-full" style={{ width: `${goalPct}%`, background: PURPLE }}></div>
                </div>
                <span className="text-xs font-bold text-slate-800">
                  {Math.min(data.invitesGoal.done, data.invitesGoal.goal)}/{data.invitesGoal.goal}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {modal === "how" ? (
        <Modal title="¿Cómo compartir tu link?" onClose={() => setModal(null)}>
          <ol className="flex flex-col gap-3 text-sm text-slate-700 list-decimal pl-5">
            <li>Copia tu enlace o descarga tu código QR.</li>
            <li>Compártelo por WhatsApp, redes o correo con uno de los mensajes sugeridos.</li>
            <li>Cuando alguien entre y se registre, aparecerá en <strong>Mis Prospectos</strong>.</li>
            <li>Escríbele con un mensaje aprobado y acompáñalo en su ruta.</li>
          </ol>
          <p className="mt-4 text-xs text-slate-500">Recuerda comunicar sin prometer ingresos ni resultados garantizados. Usa los mensajes aprobados por tu equipo.</p>
        </Modal>
      ) : null}

      {modal === "style" ? (
        <Modal title="Personalizar tu QR" onClose={() => setModal(null)}>
          <div className="flex flex-col sm:flex-row gap-5">
            <div className="w-40 h-40 flex-shrink-0 rounded-xl border border-slate-200 p-2 bg-white mx-auto">{qrUrl ? <img src={qrUrl} alt="Vista previa del QR" className="w-full h-full" /> : null}</div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-slate-600 mb-2">Color</div>
              <div className="flex gap-2 flex-wrap">
                {QR_COLORS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setQrStyle((s) => ({ ...s, color: c.value }))}
                    aria-label={c.label}
                    aria-pressed={qrStyle.color === c.value}
                    className={`w-9 h-9 rounded-full cursor-pointer border-2 ${qrStyle.color === c.value ? "border-slate-900" : "border-transparent"}`}
                    style={{ background: c.value }}
                  ></button>
                ))}
              </div>
              <label className="mt-4 flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={qrStyle.withLogo} onChange={(e) => setQrStyle((s) => ({ ...s, withLogo: e.target.checked }))} className="w-4 h-4 accent-violet-600" />
                Mostrar el logo de Vívelo al centro
              </label>
              <p className="mt-3 text-[11px] text-slate-500">Se guarda en este navegador. Verifica que tu QR se escanee bien antes de imprimirlo.</p>
            </div>
          </div>
        </Modal>
      ) : null}

      {modal === "messages" ? (
        <Modal title="Mensajes aprobados por tu equipo" onClose={() => setModal(null)}>
          <ul className="flex flex-col gap-3">
            {approved.map((m) => (
              <li key={m.id} className="rounded-xl border border-slate-200 p-3">
                <div className="text-xs font-bold text-slate-900">{m.title}</div>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{m.content}</p>
                <button onClick={() => copy(`${m.content} ${link}`, "Mensaje copiado")} className="mt-2 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold cursor-pointer">
                  <i className="ri-file-copy-line mr-1"></i>Copiar con mi link
                </button>
              </li>
            ))}
            {approved.length === 0 ? <li className="text-sm text-slate-500 text-center py-4">Todavía no hay mensajes aprobados.</li> : null}
          </ul>
        </Modal>
      ) : null}

      {toast ? <div className="fixed bottom-24 lg:bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-[#0B1F3A] text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AffiliateShell>
  );
}
